import type { QrResult } from "../../types/scan";
import { refreshAndGetToken } from "../Session";

/**
 * Extracts the hash from a QR code URL (everything after the last /).
 * e.g. "https://example.com/path/abc123" → "abc123"
 */
export function extractHashFromQrUrl(url: string): string | null {
  const trimmed = url.trim();
  if (!trimmed) return null;
  const lastSlash = trimmed.lastIndexOf("/");
  if (lastSlash === -1) return null;
  const hash = trimmed.slice(lastSlash + 1).trim();
  return hash || null;
}

export interface FetchQrUrlBackendConfig {
  baseUrl: string;
  token: string | null;
}

/**
 * Verifies the QR code by calling the backend proxy (avoids CORS).
 * Backend calls: GET https://cipapigw.cartebtp.fr/api/v1/QRCodeCarte/{hash}
 * Returns valid/invalid from the API, or pending on failure.
 */
export async function fetchQrUrl(
  url: string,
  backendConfig?: FetchQrUrlBackendConfig | null
): Promise<QrResult> {
  const hash = extractHashFromQrUrl(url);
  if (!hash) {
    return { status: "pending", url, message: "URL sans hash vérifiable" };
  }

  if (backendConfig?.baseUrl && backendConfig.token) {
    try {
      const base = backendConfig.baseUrl.replace(/\/$/, "");
      const urlToFetch = `${base}/scan/check-qr/${encodeURIComponent(hash)}`;
      let token = backendConfig.token;
      let res = await fetch(urlToFetch, {
        method: "GET",
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401) {
        const newToken = await refreshAndGetToken();
        if (newToken) {
          res = await fetch(urlToFetch, {
            method: "GET",
            headers: { Authorization: `Bearer ${newToken}` },
          });
        }
      }
      const data = await res.json().catch(() => ({}));
      const status = (data.status as QrResult["status"]) ?? "pending";
      const message = typeof data.message === "string" ? data.message : undefined;
      const btp_number = typeof data.btp_number === "string" && data.btp_number ? data.btp_number : undefined;
      return { status, message, url, btp_number };
    } catch {
      return { status: "pending", url };
    }
  }

  return { status: "pending", url, message: "Vérification non disponible" };
}
